import { observable, toJS, makeObservable } from "mobx";
import {
  fetchSnippetReports,
  fetchSnippetReport,
  approveSnippet,
  rejectSnippet,
  fetchReportLocations,
  fetchSnippetReportBedLists,
  getTeleCandidates,
} from "../../serviceCalls";
import _ from "lodash";

import FilterOptionsCompiler from "./FilterOptionsComplier.worker";
import config from "../../config";
import { DateTime } from "luxon";
import swal from "sweetalert2";
import { Status } from "./SnippetReport";

const sortDepth = 2;
export const DEFAULT_EXPORT_MESSAGE =
  "Your CSV file has been successfully exported.";

export default class SnippetReportStore {
  bedLists;
  _checkedLocations = [];
  columns = [];
  data = {};
  dataRangeInHours = 72;
  _endBed = "";
  endDate;
  expandedLocations = ["all"];
  expandHeader;
  exportEnabled = false;
  exportMessage = "";
  exportSupervisorReportPromptText = "";
  includePurgePending = false;
  isTele = false;
  loading = false;
  loadingPrefs = true;
  isTele = false;
  loadingLocations = false;
  loadingReport = false;
  locations = [];
  nestedLocations = [];
  query;
  report = null;
  selectedBedList;
  _selectedLocations = [];
  snippets = [];
  _startBed = "";
  startDate;
  teleCandidates = [];
  testValues = [];
  windowSize;

  get defaultStartDate() {
    return DateTime.local().minus({ hours: this.dataRangeInHours }).toJSDate();
  }

  get checkedLocations() {
    return this._checkedLocations;
  }

  set checkedLocations(checkedLocations) {
    if (checkedLocations[0] === "all") {
      checkedLocations[0] = JSON.stringify({ facility: "", unit: "all" });
    }

    this._checkedLocations = checkedLocations;

    this.selectedLocations = checkedLocations.map((json) => ({
      value: JSON.parse(json),
    }));
  }
  get selectedLocations() {
    if (this.selectedBedList) return [];
    return this._selectedLocations;
  }
  set selectedLocations(values) {
    this._selectedLocations = values;
  }

  selectedFacilities(locations = this._selectedLocations) {
    return locations
      .filter((l) => !l.value.unit)
      .map((l) => l.value.facility)
      .filter(Boolean);
  }
  get startBed() {
    return this._startBed;
  }
  set startBed(value) {
    this._startBed = value || "";
    this.savePrefs();
  }

  get endBed() {
    return this._endBed;
  }
  set endBed(value) {
    this._endBed = value || "";
    this.savePrefs();
  }

  approveSnippet = approveSnippet;
  rejectSnippet = rejectSnippet;

  constructor() {
    makeObservable(this, {
      bedLists: observable,
      _checkedLocations: observable,
      columns: observable,
      data: observable,
      dataRangeInHours: observable,
      _endBed: observable,
      endDate: observable,
      expandedLocations: observable,
      expandHeader: observable,
      exportEnabled: observable,
      exportMessage: observable,
      exportSupervisorReportPromptText: observable,
      includePurgePending: observable,
      isTele: observable,
      loading: observable,
      loadingPrefs: observable,
      loadingLocations: observable,
      loadingReport: observable,
      locations: observable,
      nestedLocations: observable,
      query: observable,
      report: observable,
      selectedBedList: observable,
      _selectedLocations: observable,
      snippets: observable,
      _startBed: observable,
      startDate: observable,
      testValues: observable,
      teleCandidates: observable,
      windowSize: observable,
    });

    this.setSort = this.setSort.bind(this);
    this.savePrefs = this.savePrefs.bind(this);
  }

  compileFilterOptions() {
    const filterOptionsCompiler = new FilterOptionsCompiler();
    const { columns } = this;

    const filteredColumns = columns.filter((column) => column.filterable);
    filteredColumns.forEach((column) => {
      const snippets = this.isTele ? this.teleCandidates : this.snippets;
      const columnValues = snippets.map((snippet) =>
        this.getCellValue(column, snippet),
      );
      filterOptionsCompiler.postMessage({
        columnValues,
        columnName: column.Header,
      });
    });
    this.columns = columns;

    filterOptionsCompiler.onmessage = ({ data }) => {
      const { columnName, uniqueValues } = data;

      const column = this.columns.find((c) => c.Header === columnName);

      const filterOptions = uniqueValues.map((value) => ({
        value,
        label: column.filterFormatter(value),
      }));

      if (column._filterValues) {
        if (column.single) {
          column.filterValues =
            filterOptions.find(
              (option) => option.value === column._filterValues,
            ) || null;
        } else {
          column.filterValues = column._filterValues.map((value) =>
            filterOptions.find((option) => option.value === value),
          );
        }
      }
      _.remove(filteredColumns, (c) => c === column);
      if (filteredColumns.length === 0) {
        filterOptionsCompiler.terminate();
      }
      column.filterOptions = filterOptions;
    };
  }

  get simplifiedLocations() {
    const checkedLocations = this.checkedLocations.map(JSON.parse);
    let simplifiedLocations = checkedLocations;
    this.locations[0].children.forEach((facility) => {
      if (facility.children) {
        const facilityTxt = facility._value.facility;

        if (
          facility.children
            ?.map((f) => f.value)
            .slice()
            .every((l) => this.checkedLocations.includes(l))
        ) {
          const firstItem = simplifiedLocations.find(
            (l) => l.facility === facilityTxt,
          );
          const insertIndex = simplifiedLocations.indexOf(firstItem);
          simplifiedLocations = simplifiedLocations.filter(
            (l) => l.facility !== facility._value.facility,
          );
          simplifiedLocations.splice(insertIndex, 0, { facility: facilityTxt });
        } else {
          simplifiedLocations = simplifiedLocations.filter(
            (l) => l.facility !== facilityTxt || l.unit,
          );
        }
      }
    });

    if (
      this.locations[0].children.every((l) =>
        simplifiedLocations.find((sl) => _.isEqual(sl, l._value)),
      )
    ) {
      return [];
    } else {
      return simplifiedLocations;
    }
  }

  get allChecked() {
    try {
      const locations = JSON.parse(this.query.checkedLocations[0]);
      const isAllChecked = locations.unit === "all";
      return isAllChecked;
    } catch {
      return false;
    }
  }

  fetchReports() {
    this.teleCandidates = [];

    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      swal.fire({
        title: "Invalid Date Range",
        text: "Date range must be in chronological order.",
      });
      return;
    }

    this.columns.forEach((column) => {
      column.sorted = null;
    });

    const checkedLocations = this.checkedLocations.map(JSON.parse);

    if (checkedLocations.length === 0 && !this.selectedBedList) {
      swal.fire({
        title: "Action Needed",
        text: "Please select at least one unit or a bed list",
      });

      return;
    }

    this.loading = true;

    const query = {
      startDate: this.startDate,
      endDate: this.endDate,
      includePurgePending: this.includePurgePending,
      checkedLocations: this.checkedLocations,
      simplifiedLocations: this.simplifiedLocations,
      units: _.uniq(checkedLocations.map((l) => l.unit)).join(),
      facilityCodes: _.uniq(
        checkedLocations.map((location) => location.facility).filter(Boolean),
      ).join(),
      selectedBedList: toJS(this.selectedBedList),
    };
    if (query.selectedBedList) {
      this._startBed = "";
      this._endBed = "";
    }

    return fetchSnippetReports({
      fromDate:
        (this.startDate && DateTime.fromJSDate(this.startDate).toISODate()) ||
        undefined,
      toDate:
        (this.endDate &&
          DateTime.fromJSDate(this.endDate).plus({ days: 1 }).toISODate()) ||
        undefined,
      units: query.units,
      facilityCodes: query.facilityCodes.length
        ? query.facilityCodes
        : undefined,
      includePurgePending: query.includePurgePending,
      bedListId: this.selectedBedList?.id,
    })
      .then((snippets) => {
        const facilityWhitelist = _.get(config, "snippetsReports.facilities");
        if (facilityWhitelist) {
          snippets = snippets.filter((s) =>
            facilityWhitelist.includes(s.facilityCode || ""),
          );
        }
        const locationWhitelist = _.get(config, "snippetsReports.locations");
        if (locationWhitelist) {
          snippets = snippets.filter((s) =>
            locationWhitelist.find((location) => {
              return (
                location.unit === s.unit &&
                (s.facilityCode || "") === location.facility
              );
            }),
          );
        }
        return snippets;
      })
      .then((snippets) => {
        this.snippets = snippets.map((s) => ({
          ...s,
          resolution: null,
        }));
        this.query = query;
        this.compileFilterOptions();
        this.savePrefs();
        return snippets;
      })
      .finally(() => (this.loading = false));
  }
  fetchLocations() {
    this.loadingLocations = true;

    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      swal.fire({
        title: "Invalid Date Range",
        text: "Date range must be in chronological order.",
      });
      return;
    }

    return fetchReportLocations()
      .then((locations) => {
        const facilityWhitelist = _.get(config, "snippetsReports.facilities");
        if (facilityWhitelist) {
          locations = locations.filter((l) =>
            facilityWhitelist.includes(l.facility),
          );
        }
        const locationWhitelist = _.get(config, "snippetsReports.locations");
        if (locationWhitelist) {
          locations = _.intersectionWith(
            locations,
            locationWhitelist,
            _.isEqual,
          );
        }
        return locations;
      })
      .then((res) => {
        this.facilities = _.uniq(res.map((l) => l.facility));

        const facilities = res.reduce((facilities, location) => {
          let facility = facilities.find((f) => f.label === location.facility);
          if (location.facility && !facility) {
            const value = {
              facility: location.facility,
            };
            facility = {
              label: location.facility,
              value: JSON.stringify({
                facility: location.facility,
              }),
              _value: value,
              children: [],
            };
            if (location.facility === "" && this.facilities.length > 1) {
              facility.children.pop();
            }
            facilities.push(facility);
          }
          (facility?.children || facilities).push({
            label:
              this.facilities.length > 1 && location.facility
                ? `${location.facility} - ${location.unit}`
                : location.unit,
            value: JSON.stringify(location),
            _value: location,
          });
          return facilities;
        }, []);
        this.locations = [{ label: "All", value: "all", children: facilities }];
      })
      .finally(() => (this.loadingLocations = false));
  }
  fetchBedLists() {
    return fetchSnippetReportBedLists()
      .then((res) => {
        this.bedLists = res.lists;
        return res;
      })
      .catch(() => {
        return Promise.resolve();
      });
  }

  fetchTeleCandidates() {
    this.snippets = [];
    // reset every column sorted state
    this.columns.forEach((column) => {
      column.sorted = null;
    });

    const checkedLocations = this.checkedLocations.map(JSON.parse);

    if (checkedLocations.length === 0 && !this.selectedBedList) {
      swal.fire({
        title: "Action Needed",
        text: "Please select at least one unit or a bed list",
      });

      return;
    }

    this.loading = true;

    const query = {
      startDate: DateTime.local().minus({ days: 2 }).toJSDate(),
      endDate: DateTime.local().toJSDate(),
      includePurgePending: this.includePurgePending,
      checkedLocations: this.checkedLocations,
      simplifiedLocations: this.simplifiedLocations,
      units: _.uniq(checkedLocations.map((l) => l.unit)).join(),
      facilityCodes: _.uniq(
        checkedLocations.map((location) => location.facility).filter(Boolean),
      ).join(),
      selectedBedList: toJS(this.selectedBedList),
    };
    if (query.selectedBedList) {
      this._startBed = "";
      this._endBed = "";
    }

    return getTeleCandidates({
      fromDate:
        (this.startDate && DateTime.fromJSDate(this.startDate).toISODate()) ||
        undefined,
      toDate:
        (this.endDate &&
          DateTime.fromJSDate(this.endDate).plus({ days: 1 }).toISODate()) ||
        undefined,
      units: query.units,
      facilityCodes: query.facilityCodes.length
        ? query.facilityCodes
        : undefined,
      includePurgePending: query.includePurgePending,
      bedListId: this.selectedBedList?.id,
    })
      .then((candidates) => {
        this.teleCandidates = candidates.map((s) => ({
          ...s,
          resolution: null,
        }));
        this.query = query;
        this.compileFilterOptions();
        this.savePrefs();
        return candidates;
      })
      .finally(() => (this.loading = false));
  }

  get filteredSnippets() {
    const filteredColumns = this.columns.filter((column) =>
      _.get(column.filterValues, "length"),
    );
    let snippets = this.isTele
      ? this.teleCandidates
      : this.snippets
          .filter((s) =>
            this.startBed
              ? s.location &&
                s.location.toString().trim().toLowerCase() >=
                  this.startBed.trim().toLowerCase()
              : true,
          )
          .filter((s) =>
            this.endBed
              ? s.location &&
                s.location.toString().trim().toLowerCase() <=
                  this.endBed.trim().toLowerCase()
              : true,
          )
          .filter((snippet) => {
            // multi option filters
            if (filteredColumns.length) {
              const missingColumn = filteredColumns.find(
                (column) =>
                  !column.filterValues
                    .map((filter) => filter?.value)
                    .filter(Boolean)
                    .includes(this.getCellValue(column, snippet)),
              );
              if (missingColumn) return false;
            }
            return true;
          })
          .filter(
            (
              snippet, // single option filters
            ) =>
              this.columns
                .filter((c) => c.single && _.get(c.filterValues, "value"))
                .reduce(
                  (acc, c) =>
                    acc &&
                    c.filterValues.value === this.getCellValue(c, snippet),
                  true,
                ),
          );

    for (let priority = sortDepth; priority > 0; priority--) {
      const sortColumn = this.columns.find(
        (c) => c.sorted?.priority === priority,
      );
      if (sortColumn) {
        const descending = !(sortColumn.sorted.order === "ASC");
        const comparator = sortColumn.comparator
          ? (a, b) => sortColumn.comparator(a, b) * (descending ? 1 : -1)
          : alphaNumericCompareBy({
              accessor: (snippet) => this.getCellValue(sortColumn, snippet),
              descending,
            });
        snippets = snippets.slice().sort((a, b) => comparator(a, b));
      }
    }
    return snippets;
  }

  getCellValue(column, row) {
    let value;
    if (typeof column.accessor === "function") {
      value = column.accessor(row);
    } else if (typeof column.accessor === "string") {
      value = _.get(row, column.accessor);
    }

    if (value === null || value === undefined) {
      return "";
    } else {
      return value;
    }
  }

  setSort(column) {
    if (!column.sortable) return;

    if (_.get(column.sorted, "priority") === 1) {
      column.sorted.order = column.sorted.order === "ASC" ? "DESC" : "ASC";
      return;
    }

    this.columns.forEach((_column) => {
      if (_column.sorted) {
        _column.sorted.priority += 1;
        if (_column.sorted.priority > sortDepth) {
          _column.sorted = null;
        }
      }
    });

    column.sorted = {
      priority: 1,
      order: "ASC",
    };
  }

  fetchSnippetReport(params) {
    this.loadingReport = true;
    return fetchSnippetReport(params)
      .then((res) => (this.report = res))
      .finally(() => (this.loadingReport = false));
  }

  loadPrefs(prefs) {
    this.exportMessage =
      prefs?.userPreferences?.server?.pm?.snippetsReportExportMessage ||
      DEFAULT_EXPORT_MESSAGE;
    this.prefs = prefs?.userPreferences?.client?.snippetsReport || {};
    if (!Array.isArray(this.prefs.checkedLocations?.slice())) {
      this.prefs.checkedLocations = [];
    }
    this.dataRangeInHours =
      prefs?.userPreferences?.server?.pm?.dataRangeInHours ?? 72;
    if (this.prefs.columns) {
      this.prefs.columns.forEach((prefColumn) => {
        const column = this.columns.find(
          (column) => column.Header === prefColumn.Header,
        );
        if (column) Object.assign(column, prefColumn);
      });
    }
    try {
      const startDate = this.prefs?.dateFilter?.start;
      if (startDate) this.startDate = new Date(startDate);
      else this.startDate = this.defaultStartDate;
      const endDate = this.prefs?.dateFilter?.end;
      if (endDate) this.endDate = new Date(endDate);
    } catch (e) {
      console.error(e);
    }

    this._startBed = this.prefs?.bedFilter?.start ?? "";
    this._endBed = this.prefs?.bedFilter?.end ?? "";
    this.selectedBedList = this.bedLists?.find(
      (b) => b.id === this.prefs?.selectedBedListId,
    );
    this.checkedLocations = this.prefs.checkedLocations;
    this.loadingPrefs = false;
    this.includePurgePending = prefs?.includePurgePending || false;
  }

  savePrefs() {
    if (!this.setPref) {
      return;
    }
    this.setPref("snippetsReport", {
      columns: this.columns.map((column) => ({
        ..._.pick(column, ["Header", "visible"]),
        _filterValues: column.single
          ? _.get(column.filterValues, "value")
          : column.filterValues.map((f) => f?.value).filter(Boolean),
      })),
      dateFilter: { start: this.startDate, end: this.endDate },
      bedFilter: { start: this.startBed, end: this.endBed },
      selectedBedListId: this.query?.selectedBedList?.id,
      checkedLocations: this.query?.checkedLocations,
      includePurgePending: this.query?.includePurgePending,
    });
  }

  /**
   *
   * @param {String} summary
   * @returns {Array} - @ dimensional array of Strings
   */
  compileCSV(summary) {
    const rows = this.filteredSnippets.map((snippet) => {
      // the first element is empty, to reserve an empty column for the summary
      return [
        "",
        ...this.columns
          .filter((col) => col.visible)
          .map((col) => {
            let accessor = col.accessor;
            if (accessor === "status") {
              return Status({ status: snippet[accessor] });
            } else if (
              typeof accessor === "string" ||
              accessor instanceof String
            ) {
              return snippet[accessor] || "";
            } else {
              return accessor(snippet) || "";
            }
          }),
      ];
    });

    const headerRow = this.columns
      ?.filter((col) => col.visible)
      .map((col) => col.Header);
    headerRow.unshift("Meta");

    if (!rows[0]) {
      rows[0] = [""];
    }
    rows[0][0] = summary;

    rows.unshift(headerRow);
    return rows;
  }
}

export function isNullish(x) {
  return (
    typeof x === "undefined" ||
    x === null ||
    x === "" ||
    (typeof x === "number" && isNaN(x))
  );
}

/**
 * @name alphaNumericCompareBy
 *
 * constructs a comparator function
 * given an accessor. The comparator will compare string and
 * numeric values, case-insensitive, and compound string and numeric values.
 *
 * @returns {function} a comparator function to be passed to Array.sort()
 */
export function alphaNumericCompareBy({
  accessor = (x) => x,
  descending,
} = {}) {
  const directionNum = descending ? -1 : 1;
  return (aRecord, bRecord) =>
    ((aRecord, bRecord) => {
      const a = accessor(aRecord);
      const b = accessor(bRecord);
      const nullA = isNullish(a);
      const nullB = isNullish(b);
      if (nullA || nullB) return nullA - nullB;
      // This regex extracts each consecutive strings of letters,
      // and each consecutive string of numeric characters.
      // \u00C0-\u017F is the character for diacritic characters.
      // This will need to be expanded to support for foreign languages. (Japanese)
      const regex = /([a-zA-Z\u00C0-\u017F]+|(($-)?\d+))/g;
      const aMatches = String(a).match(regex) || [];
      const bMatches = String(b).match(regex) || [];
      for (let i = 0; i < aMatches.length; i++) {
        const aPart = aMatches[i].toLowerCase();
        const bPart = bMatches[i]?.toLowerCase();
        if (bPart === undefined) return 1;
        if (aPart === bPart) continue;
        const [aNumber, bNumber] = [aPart, bPart].map(Number);
        const typeDifference = isNaN(aNumber) - isNaN(bNumber); // bitwise
        if (typeDifference) return typeDifference;
        if (!isNaN(aNumber)) {
          return aNumber - bNumber;
        }
        return aPart < bPart ? -1 : 1;
      }
      if (bMatches.length > aMatches.length) return -1;
      return 0;
    })(aRecord, bRecord) * directionNum;
}
